<template>
  <div class="van-dropdown-menu">
    <div class="van-dropdown-menu__bar">
      <!-- 前4个筛选条件 -->
      <div
        v-for="(filter, index) in visibleFilters"
        :key="filter.label_id"
        :class="['van-dropdown-menu__item', { 'van-dropdown-menu__item--active': activeIndex === index }]"
        @click="toggleItem(index)"
      >
        <span :class="['van-dropdown-menu__title', { 'van-dropdown-menu__title--active': activeIndex === index || hasSelectedValue(filter) }]">
          <span class="van-dropdown-menu__title-text">{{ getTruncatedTitle(filter.label_name) }}</span>
          <ChevronDown :size="14" :class="['van-dropdown-menu__title-icon', { 'van-dropdown-menu__title-icon--active': activeIndex === index }]" />
        </span>
      </div>

      <!-- 更多按钮（如果超过4个，排除 input 类型） -->
      <div
        v-if="sortedFilters.filter(f => f.search_type !== 'input').length > 4"
        :class="['van-dropdown-menu__item', 'van-dropdown-menu__item--more', { 'van-dropdown-menu__item--active': activeIndex === 'more' }]"
        @click="toggleItem('more')"
      >
        <span :class="['van-dropdown-menu__title', { 'van-dropdown-menu__title--active': activeIndex === 'more' }]">
          <Filter :size="16" class="van-dropdown-menu__more-icon" />
        </span>
      </div>
    </div>

    <!-- Input 类型筛选条件 - 搜索框样式 -->
    <div v-if="inputFilters.length > 0" class="mobile-filter-search-boxes">
      <div
        v-for="filter in inputFilters"
        :key="filter.label_id"
        class="mobile-filter-search-item"
      >
        <a-input
          :value="filterValues[filter.field_name]"
          :placeholder="`请输入${filter.label_name}`"
          size="large"
          class="mobile-filter-search-input"
          @input="handleInputChange(filter.field_name, $event.target.value)"
          @pressEnter="handleSearchAndClose"
        >
          <template #prefix>
            <SearchIcon :size="16" />
          </template>
        </a-input>
      </div>
    </div>

    <!-- 已选筛选条件展示 -->
    <div v-if="hasActiveFilters" class="mobile-filter-active-tags">
      <div class="active-filters-header" @click="toggleActiveFilters">
        <span class="active-filters-title">已选筛选条件</span>
        <ChevronDown 
          :size="16" 
          :class="['active-filters-chevron', { 'active-filters-chevron--expanded': isActiveFiltersExpanded }]"
        />
      </div>
      <div v-show="isActiveFiltersExpanded" class="active-filters-content">
        <div
          v-for="filter in sortedFilters.filter(f => hasCommittedValue(f))"
          :key="filter.label_id"
          class="active-filter-group"
        >
          <div class="active-filter-label">{{ filter.label_name }}：</div>
          <div class="active-filter-values">
            <a-tag
              v-for="(value, index) in getCommittedValues(filter)"
              :key="index"
              class="active-filter-tag"
            >
              {{ getCommittedValueLabel(filter, value) }}
            </a-tag>
          </div>
        </div>
      </div>
    </div>

    <!-- 下拉内容 -->
    <div
      v-if="activeIndex !== null"
      class="van-dropdown-menu__content"
    >
        <!-- 前4个筛选条件的内容 -->
        <template v-if="typeof activeIndex === 'number' && visibleFilters[activeIndex]">
          <div class="van-dropdown-menu__content-inner">
            <!-- 选项类型（TagFilter，支持多选） -->
            <div v-if="visibleFilters[activeIndex].search_type === 'option'" class="filter-content-item">
              <TagFilter
                :model-value="filterValues[visibleFilters[activeIndex].field_name]"
                :options="visibleFilters[activeIndex].options"
                :show-more-count="getShowMoreCount(visibleFilters[activeIndex])"
                @update:modelValue="handleFilterChange(visibleFilters[activeIndex].field_name, $event)"
              />
            </div>

            <!-- 单选类型（TagFilter，单选模式） -->
            <div v-else-if="visibleFilters[activeIndex].search_type === 'radio'" class="filter-content-item">
              <TagFilter
                :model-value="filterValues[visibleFilters[activeIndex].field_name]"
                :options="visibleFilters[activeIndex].options"
                :show-more-count="getShowMoreCount(visibleFilters[activeIndex])"
                :single="true"
                @update:modelValue="handleFilterChange(visibleFilters[activeIndex].field_name, $event)"
              />
            </div>

            <!-- 树形类型（TreeFilter，支持多选） -->
            <div v-else-if="visibleFilters[activeIndex].search_type === 'tree'" class="filter-content-item">
              <TreeFilter
                :model-value="filterValues[visibleFilters[activeIndex].field_name]"
                :options="visibleFilters[activeIndex].options"
                @update:modelValue="handleFilterChange(visibleFilters[activeIndex].field_name, $event)"
              />
            </div>
          </div>
        </template>

        <!-- 更多筛选条件的内容 - 显示所有筛选项 -->
        <template v-else-if="activeIndex === 'more'">
          <div class="van-dropdown-menu__content-inner">
            <div
              v-for="filter in sortedFilters"
              :key="filter.label_id"
              class="more-filter-item"
            >
              <div class="more-filter-label">{{ filter.label_name }}</div>
              <div class="more-filter-content">
                <!-- 输入框类型 -->
                <a-input
                  v-if="filter.search_type === 'input'"
                  :value="filterValues[filter.field_name]"
                  :placeholder="`请输入${filter.label_name}`"
                  size="large"
                  @input="handleInputChange(filter.field_name, $event.target.value)"
                />

                <!-- 选项类型（TagFilter，支持多选） -->
                <TagFilter
                  v-else-if="filter.search_type === 'option'"
                  :model-value="filterValues[filter.field_name]"
                  :options="filter.options"
                  :show-more-count="getShowMoreCount(filter)"
                  @update:modelValue="handleFilterChange(filter.field_name, $event)"
                />

                <!-- 单选类型（TagFilter，单选模式） -->
                <TagFilter
                  v-else-if="filter.search_type === 'radio'"
                  :model-value="filterValues[filter.field_name]"
                  :options="filter.options"
                  :show-more-count="getShowMoreCount(filter)"
                  :single="true"
                  @update:modelValue="handleFilterChange(filter.field_name, $event)"
                />

                <!-- 树形类型（TreeFilter，支持多选） -->
                <TreeFilter
                  v-else-if="filter.search_type === 'tree'"
                  :model-value="filterValues[filter.field_name]"
                  :options="filter.options"
                  @update:modelValue="handleFilterChange(filter.field_name, $event)"
                />
              </div>
            </div>
          </div>
        </template>

        <!-- 操作按钮 -->
        <div class="van-dropdown-menu__actions">
          <a-button block type="primary" size="large" class="action-button action-button-primary" @click="handleSearchAndClose">
            检索
          </a-button>
          <a-button block size="large" class="action-button action-button-secondary" @click="handleResetAndClose">
            重置
          </a-button>
          <a-button block size="large" class="action-button action-button-secondary" @click="$emit('advanced-search')">
            高级检索
          </a-button>
        </div>
      </div>

    <!-- 遮罩层 - 放在下拉内容之后，确保下拉内容在上层 -->
    <div
      v-if="activeIndex !== null"
      class="van-dropdown-menu__overlay"
      @click="closeMenu"
    />
  </div>
</template>

<script>
import { ChevronDown, Filter, Search as SearchIcon } from 'lucide-vue-next'
import TagFilter from './TagFilter.vue'
import TreeFilter from './TreeFilter.vue'
import { isMobileDevice } from '@/utils/deviceDetection'

export default {
  name: 'MobileFilterContainer',
  components: {
    ChevronDown,
    Filter,
    SearchIcon,
    TagFilter,
    TreeFilter
  },
  props: {
    filters: {
      type: Array,
      required: true
    },
    filterValues: {
      type: Object,
      required: true
    },
    labelGroup: {
      type: Number,
      required: true
    }
  },
  emits: ['search', 'reset', 'filter-change', 'input-change', 'advanced-search'],
  data() {
    return {
      activeIndex: null, // null | number | 'more'
      isMobile: false,
      isActiveFiltersExpanded: true, // 默认展开
      committedFilterValues: {} // 已提交的筛选条件（点击检索后的值）
    }
  },
  watch: {
    labelGroup: {
      handler(newVal, oldVal) {
        // 切换菜单时，关闭筛选弹层并清空已提交的筛选条件
        if (oldVal && oldVal !== newVal) {
          this.activeIndex = null
          this.committedFilterValues = {}
        }
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.checkMobile)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.checkMobile)
  },
  computed: {
    sortedFilters() {
      let sorted = [...this.filters]
      
      // 企业/市场端页面（labelGroup 为 1）需要特殊排序
      if (this.labelGroup === 1) {
        const priorityOrder = ['企业', '疾病领域', '设备类型', '时间范围']
        sorted = sorted.sort((a, b) => {
          const aIndex = priorityOrder.indexOf(a.label_name)
          const bIndex = priorityOrder.indexOf(b.label_name)
          if (aIndex !== -1 && bIndex !== -1) {
            return aIndex - bIndex
          }
          if (aIndex !== -1) return -1
          if (bIndex !== -1) return 1
          return a.order - b.order
        })
      } else {
        sorted = sorted.sort((a, b) => a.order - b.order)
      }
      
      return sorted
    },
    visibleFilters() {
      // 排除 input 类型的筛选条件
      return this.sortedFilters.filter(f => f.search_type !== 'input').slice(0, 4)
    },
    moreFilters() {
      // 排除 input 类型的筛选条件
      return this.sortedFilters.filter(f => f.search_type !== 'input').slice(4)
    },
    inputFilters() {
      // 只返回 input 类型的筛选条件
      return this.sortedFilters.filter(f => f.search_type === 'input')
    },
    hasActiveFilters() {
      // 排除 input 类型，只检查其他类型的筛选条件
      // 使用已提交的筛选条件（committedFilterValues）而不是当前的 filterValues
      return this.sortedFilters.filter(f => f.search_type !== 'input').some(filter => this.hasCommittedValue(filter))
    }
  },
  methods: {
    toggleActiveFilters() {
      this.isActiveFiltersExpanded = !this.isActiveFiltersExpanded
    },
    toggleItem(index) {
      if (this.activeIndex === index) {
        this.activeIndex = null
      } else {
        this.activeIndex = index
      }
    },
    closeMenu() {
      this.activeIndex = null
    },
    clearCommittedFilters() {
      this.committedFilterValues = {}
    },
    handleFilterChange(fieldName, value) {
      this.$emit('filter-change', fieldName, value)
    },
    handleInputChange(fieldName, value) {
      this.$emit('input-change', fieldName, value)
    },
    handleSearchAndClose() {
      // 保存当前筛选条件到已提交的筛选条件
      this.committedFilterValues = { ...this.filterValues }
      this.closeMenu()
      this.$emit('search')
    },
    handleResetAndClose() {
      // 清空已提交的筛选条件
      this.committedFilterValues = {}
      this.closeMenu()
      this.$emit('reset')
    },
    getShowMoreCount(filter) {
      if (filter.field_name === 'enterprise') {
        return 12
      }
      if (filter.field_name === 'disease' && this.labelGroup === 5) {
        return 12
      }
      return 10
    },
    hasSelectedValue(filter) {
      // 检查是否有选中值，用于激活状态显示
      const value = this.filterValues[filter.field_name]
      if (!value) return false
      
      if (filter.search_type === 'option' || filter.search_type === 'tree') {
        return Array.isArray(value) && value.length > 0
      } else if (filter.search_type === 'radio') {
        return value !== '' && value !== filter.default_value
      } else if (filter.search_type === 'input') {
        return value !== '' && value !== filter.default_value
      }
      
      return false
    },
    findOptionInTree(options, targetId) {
      for (const option of options || []) {
        if (option.id === targetId || option.value === targetId) {
          return option
        }
        if (option.children) {
          const found = this.findOptionInTree(option.children, targetId)
          if (found) return found
        }
      }
      return null
    },
    getTruncatedTitle(title) {
      // 最多显示4个汉字
      if (!title) return ''
      if (title.length <= 4) return title
      return title.substring(0, 4)
    },
    checkMobile() {
      this.isMobile = isMobileDevice()
    },
    getSelectedValues(filter) {
      const value = this.filterValues[filter.field_name]
      if (!value) return []
      
      if (filter.search_type === 'option' || filter.search_type === 'tree') {
        return Array.isArray(value) ? value : [value]
      } else if (filter.search_type === 'radio') {
        return value ? [value] : []
      } else if (filter.search_type === 'input') {
        return value ? [value] : []
      }
      
      return []
    },
    getValueLabel(filter, value) {
      // 对于选项类型，尝试从 options 中找到对应的 label
      if (filter.search_type === 'option' || filter.search_type === 'radio') {
        const option = this.findOptionInList(filter.options, value)
        if (option) {
          return option.option_name || option.name || value
        }
      } else if (filter.search_type === 'tree') {
        const option = this.findOptionInTree(filter.options, value)
        if (option) {
          return option.name || value
        }
      }
      
      return value
    },
    findOptionInList(options, targetValue) {
      for (const option of options || []) {
        if (option.option_value === targetValue || option.option_id === targetValue) {
          return option
        }
      }
      return null
    },
    // 检查已提交的筛选条件是否有值
    hasCommittedValue(filter) {
      const value = this.committedFilterValues[filter.field_name]
      if (!value) return false
      
      if (filter.search_type === 'option' || filter.search_type === 'tree') {
        return Array.isArray(value) && value.length > 0
      }
      if (filter.search_type === 'radio') {
        return value !== '' && value !== filter.default_value
      }
      if (filter.search_type === 'input') {
        return value !== '' && value !== filter.default_value
      }
      return false
    },
    // 获取已提交的筛选条件的值数组
    getCommittedValues(filter) {
      const value = this.committedFilterValues[filter.field_name]
      if (!value) return []
      
      if (filter.search_type === 'option' || filter.search_type === 'tree') {
        return Array.isArray(value) ? value : [value]
      }
      if (filter.search_type === 'radio') {
        return value ? [value] : []
      }
      if (filter.search_type === 'input') {
        return value ? [value] : []
      }
      return []
    },
    // 获取已提交的筛选条件的标签
    getCommittedValueLabel(filter, value) {
      if (filter.search_type === 'option' || filter.search_type === 'radio') {
        const option = this.findOptionInList(filter.options, value)
        if (option) {
          return option.option_name || option.name || value
        }
        // 如果是树形结构，尝试查找
        if (filter.options && filter.options.length > 0 && filter.options[0].children) {
          const found = this.findOptionInTree(filter.options, value)
          if (found) {
            return found.option_name || found.name || value
          }
        }
        return value
      }
      return value
    }
  }
}
</script>

<style scoped>
/* Vant 风格的下拉菜单 */
.van-dropdown-menu {
  position: sticky;
  top: 0;
  background-color: #fff;
  user-select: none;
  z-index: 1000;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  border: none;
}

.van-dropdown-menu__bar {
  position: relative;
  display: flex;
  height: 48px;
  background-color: #fff;
  box-shadow: 0 2px 12px rgba(100, 101, 102, 0.12);
  flex-shrink: 0;
}

.van-dropdown-menu__item {
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  min-width: 0;
  cursor: pointer;
  position: relative;
}

.van-dropdown-menu__item:active {
  opacity: 0.7;
}

.van-dropdown-menu__item--active {
  color: #1989fa;
}

.van-dropdown-menu__title {
  position: relative;
  box-sizing: border-box;
  max-width: 100%;
  padding: 0 8px;
  color: #323233;
  font-size: 15px;
  line-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.van-dropdown-menu__title--active {
  color: #1989fa;
}

.van-dropdown-menu__title-text {
  white-space: nowrap;
  flex: 0 0 auto;
  max-width: 4em;
}


.van-dropdown-menu__title-icon {
  margin-left: 4px;
  transition: transform 0.3s;
  color: #969799;
  flex-shrink: 0;
}

.van-dropdown-menu__item--more {
  flex: 0 0 auto;
  min-width: 48px;
}

.van-dropdown-menu__more-icon {
  color: #323233;
  transition: color 0.3s;
}

.van-dropdown-menu__item--more.van-dropdown-menu__item--active .van-dropdown-menu__more-icon {
  color: #1989fa;
}

.van-dropdown-menu__title-icon--active {
  transform: rotate(180deg);
  color: #1989fa;
}

.van-dropdown-menu__overlay {
  position: fixed;
  top: 112px;
  left: 0;
  width: 100%;
  height: calc(100vh - 112px);
  background-color: rgba(0, 0, 0, 0.7);
  z-index: 1000;
  pointer-events: auto;
}

.van-dropdown-menu__content {
  position: fixed;
  top: 112px;
  left: 0;
  right: 0;
  max-height: calc(100vh - 112px);
  background-color: #fff;
  z-index: 1001;
  overflow-y: auto;
  box-shadow: 0 2px 12px rgba(100, 101, 102, 0.12);
  pointer-events: auto;
  display: flex;
  flex-direction: column;
  -webkit-overflow-scrolling: touch;
}

.van-dropdown-menu__content-inner {
  padding: 0;
  flex: 1;
  overflow: visible;
}

.filter-content-item {
  width: 100%;
  padding: 16px;
}

.filter-content-item:first-child {
  padding-top: 16px;
}

.more-filter-item {
  margin-bottom: 0;
  padding: 16px;
  border-bottom: 1px solid #ebedf0;
}

.more-filter-item:last-child {
  margin-bottom: 0;
  padding-bottom: 16px;
  border-bottom: none;
}

.more-filter-label {
  font-size: 15px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
  margin-bottom: 12px;
}

.more-filter-content {
  width: 100%;
}

.van-dropdown-menu__actions {
  padding: 16px;
  border-top: 1px solid #ebedf0;
  display: flex;
  gap: 12px;
  background: #fff;
  position: sticky;
  bottom: 0;
}

.van-dropdown-menu__actions .ant-btn {
  flex: 1;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  border-radius: 6px;
}

.action-button-primary {
  background: #1989fa;
  border: none;
  box-shadow: 0 2px 8px rgba(25, 137, 250, 0.3);
}

.action-button-primary:active {
  background: #096dd9;
  box-shadow: 0 1px 4px rgba(25, 137, 250, 0.3);
}

.action-button-secondary {
  background: #fff;
  border: 1px solid #ebedf0;
  color: rgba(0, 0, 0, 0.85);
}

.action-button-secondary:active {
  background: #f7f8fa;
  border-color: #dcdee0;
}

/* 已选筛选条件展示 */
.mobile-filter-active-tags {
  background: #f7f8fa;
  border-bottom: 1px solid #ebedf0;
  flex-shrink: 0;
}

.active-filters-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  user-select: none;
}

.active-filters-title {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.85);
}

.active-filters-chevron {
  transition: transform 0.3s ease;
  color: rgba(0, 0, 0, 0.45);
}

.active-filters-chevron--expanded {
  transform: rotate(180deg);
}

.active-filters-content {
  padding: 0 16px 12px 16px;
}

.active-filter-group {
  display: flex;
  align-items: flex-start;
  margin-bottom: 8px;
  flex-wrap: wrap;
}

.active-filter-group:last-child {
  margin-bottom: 0;
}

.active-filter-label {
  flex-shrink: 0;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.65);
  font-weight: 500;
  margin-right: 8px;
  line-height: 28px;
}

.active-filter-values {
  flex: 1;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.active-filter-tag {
  margin: 0;
  font-size: 12px;
  height: 28px;
  line-height: 26px;
  padding: 0 8px;
  background: #fff;
  border: 1px solid #d9d9d9;
  color: rgba(0, 0, 0, 0.85);
}

/* Input 类型筛选条件 - 搜索框样式 */
.mobile-filter-search-boxes {
  background: #fff;
  padding: 12px 16px;
  border-bottom: 1px solid #ebedf0;
  flex-shrink: 0;
}

.mobile-filter-search-item {
  margin-bottom: 12px;
}

.mobile-filter-search-item:last-child {
  margin-bottom: 0;
}

.mobile-filter-search-input {
  width: 100%;
}

.mobile-filter-search-input :deep(.ant-input) {
  border-radius: 6px;
  font-size: 14px;
}

.mobile-filter-search-input :deep(.ant-input-prefix) {
  color: rgba(0, 0, 0, 0.45);
}
</style>
